import { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

/**
 * Copy Message Modal
 * Shows a modal with pre-formatted share message that users can copy
 * Used as fallback for devices that don't support native sharing
 */

const CopyMessageModal = ({ isOpen, onClose, message, title = "Share Your Results", onCopySuccess }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);

      // Call the onCopySuccess callback if provided (e.g., to open Discord)
      if (onCopySuccess) {
        await onCopySuccess();
      }

      setTimeout(() => {
        setCopied(false);
        // Auto-close after successful copy
        setTimeout(() => onClose(), 500);
      }, 1500);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);

        // Call the onCopySuccess callback if provided (e.g., to open Discord)
        if (onCopySuccess) {
          await onCopySuccess();
        }

        setTimeout(() => {
          setCopied(false);
          setTimeout(() => onClose(), 500);
        }, 1500);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message Box */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Your share message (tap to select and copy):
          </p>
          <textarea
            readOnly
            value={message}
            onClick={(e) => e.target.select()}
            className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg p-4 min-h-[140px] max-h-[300px] text-gray-800 whitespace-pre-wrap font-sans text-sm resize-none focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <p className="text-xs text-gray-500 mt-1">
            Tap the text above to select it, or use the button below to copy automatically
          </p>
        </div>

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          disabled={copied}
          className={`
            w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold
            transition-all duration-200
            ${copied
              ? 'bg-green-500 text-white cursor-default'
              : 'bg-blue-600 hover:bg-blue-700 text-white active:scale-95'
            }
          `}
        >
          {copied ? (
            <>
              <Check className="w-5 h-5" />
              Copied to Clipboard!
            </>
          ) : (
            <>
              <Copy className="w-5 h-5" />
              Copy Message
            </>
          )}
        </button>

        {/* Instructions */}
        <p className="text-xs text-gray-500 text-center mt-4">
          After copying, you can paste this message into SMS, WhatsApp, Messenger, or any other app
        </p>
      </div>
    </div>
  );
};

export default CopyMessageModal;
